/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect, useLayoutEffect, useState } from "react";
import {
  useParams,
  Link,
  useLocation,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import BioIndexExploreLevelLayout from "../components/BioIndexExploreLevelLayout";
import useAdminClaim from "../hooks/useAdminClaim";
import { db, auth } from "../firebase";
import { taxonomicSpeciesRoute } from "../utils/taxonomicRoutes";
import SpeciesPhotoModal from "../components/SpeciesPhotoModal";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

function defaultFirstSort(a, b) {
  const ad = a?.isDefault ? 1 : 0;
  const bd = b?.isDefault ? 1 : 0;
  if (bd !== ad) return bd - ad;
  return 0;
}

function getDefaultForm(forms) {
  if (!Array.isArray(forms) || forms.length === 0) return null;
  return forms.find((f) => f?.isDefault) || forms[0];
}

function normalizeSpeciesName(name) {
  return String(name || "").trim().toLowerCase();
}

export default function SpeciesPage() {
  const {
    kingdomName,
    phylumName,
    className,
    orderName,
    familyName,
    genusName,
    uid,
  } = useParams();

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAdmin, adminLoading } = useAdminClaim();

  const [browseMode, setBrowseMode] = useState("taxonomy");
  const currentUID = auth.currentUser?.uid;
  const dataUID = uid || currentUID;
  const isOwnCollection = dataUID === currentUID;

  const adminModeRequested = searchParams.get("adminMode") === "true";

  const waitingForAdminExploreCheck =
    adminModeRequested && !isOwnCollection && adminLoading;

  const isAdminExploringCollection =
    adminModeRequested && !adminLoading && isAdmin && !isOwnCollection;

  const [speciesList, setSpeciesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [speciesRefreshKey, setSpeciesRefreshKey] = useState(0);
  const [blockRelationshipLoading, setBlockRelationshipLoading] =
    useState(!isOwnCollection);
  const [collectionInteractionBlocked, setCollectionInteractionBlocked] =
    useState(false);

  const collectionAccessBlocked =
    !isAdminExploringCollection && collectionInteractionBlocked;

  const [loadedImages, setLoadedImages] = useState({});
  const [previewPhotoBySpecies, setPreviewPhotoBySpecies] = useState({});
  const [previewSlideBySpecies, setPreviewSlideBySpecies] = useState({});

  const [selected, setSelected] = useState(null);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [friendSpeciesByName, setFriendSpeciesByName] = useState({});
  const [friendProfiles, setFriendProfiles] = useState({});
  const [friendSpeciesLoading, setFriendSpeciesLoading] = useState(false);

  useEffect(() => {
    if (
      !currentUID ||
      !dataUID ||
      isOwnCollection ||
      isAdminExploringCollection
    ) {
      setCollectionInteractionBlocked(false);
      setBlockRelationshipLoading(false);
      return undefined;
    }

    if (waitingForAdminExploreCheck) {
      return undefined;
    }

    let outgoingBlocksLoaded = false;
    let incomingBlocksLoaded = false;
    let outgoingBlockExists = false;
    let incomingBlockExists = false;

    function updateBlockRelationship() {
      setCollectionInteractionBlocked(
        outgoingBlockExists || incomingBlockExists,
      );

      if (outgoingBlocksLoaded && incomingBlocksLoaded) {
        setBlockRelationshipLoading(false);
      }
    }

    setBlockRelationshipLoading(true);

    const outgoingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockerUid", "==", currentUID),
    );

    const incomingBlocksQuery = query(
      collection(db, "userBlocks"),
      where("blockedUid", "==", currentUID),
    );

    const unsubscribeOutgoingBlocks = onSnapshot(
      outgoingBlocksQuery,
      (snapshot) => {
        outgoingBlockExists = snapshot.docs.some(
          (blockDocument) => blockDocument.data().blockedUid === dataUID,
        );
        outgoingBlocksLoaded = true;
        updateBlockRelationship();
      },
      (error) => {
        console.error("Failed to load outgoing collection blocks:", error);
        outgoingBlockExists = true;
        outgoingBlocksLoaded = true;
        updateBlockRelationship();
      },
    );

    const unsubscribeIncomingBlocks = onSnapshot(
      incomingBlocksQuery,
      (snapshot) => {
        incomingBlockExists = snapshot.docs.some(
          (blockDocument) => blockDocument.data().blockerUid === dataUID,
        );
        incomingBlocksLoaded = true;
        updateBlockRelationship();
      },
      (error) => {
        console.error("Failed to load incoming collection blocks:", error);
        incomingBlockExists = true;
        incomingBlocksLoaded = true;
        updateBlockRelationship();
      },
    );

    return () => {
      unsubscribeOutgoingBlocks();
      unsubscribeIncomingBlocks();
    };
  }, [
    currentUID,
    dataUID,
    isOwnCollection,
    isAdminExploringCollection,
    waitingForAdminExploreCheck,
  ]);

  useEffect(() => {
    let cancelled = false;

    async function loadFriendSpeciesForGenus() {
      setFriendSpeciesLoading(true);

      try {
        const normalizedGenus =
          genusName.charAt(0).toUpperCase() + genusName.slice(1);

        const outgoingBlocksQuery = query(
          collection(db, "userBlocks"),
          where("blockerUid", "==", currentUID),
        );

        const incomingBlocksQuery = query(
          collection(db, "userBlocks"),
          where("blockedUid", "==", currentUID),
        );

        const [friendsSnap, ownProfileSnap, outgoingSnap, incomingSnap] =
          await Promise.all([
            getDocs(collection(db, "users", currentUID, "friends")),
            getDoc(doc(db, "profile", currentUID)),
            getDocs(outgoingBlocksQuery),
            getDocs(incomingBlocksQuery),
          ]);

        const blockedUIDs = new Set([
          ...outgoingSnap.docs
            .map((blockDocument) => blockDocument.data().blockedUid)
            .filter(Boolean),
          ...incomingSnap.docs
            .map((blockDocument) => blockDocument.data().blockerUid)
            .filter(Boolean),
        ]);

        const friendUIDs = friendsSnap.docs
          .map((friendDocument) => friendDocument.id)
          .filter((friendUID) => !blockedUIDs.has(friendUID));

        const friendRows = await Promise.all(
          friendUIDs.map(async (friendUID) => {
            const [profileSnap, speciesSnap] = await Promise.all([
              getDoc(doc(db, "profile", friendUID)),
              getDocs(
                query(
                  collection(db, "users", friendUID, "species"),
                  where("genus", "==", normalizedGenus),
                ),
              ),
            ]);

            return {
              friendUID,
              profile: profileSnap.exists() ? profileSnap.data() : {},
              species: speciesSnap.docs.map((speciesDocument) => ({
                id: speciesDocument.id,
                ...speciesDocument.data(),
              })),
            };
          }),
        );

        const nextProfiles = ownProfileSnap.exists()
          ? { [currentUID]: ownProfileSnap.data() }
          : {};
        const nextFriendSpeciesByName = {};

        friendRows.forEach(({ friendUID, profile, species }) => {
          nextProfiles[friendUID] = profile;

          species.forEach((friendSpecies) => {
            const speciesKey = normalizeSpeciesName(friendSpecies.name);
            if (!speciesKey) return;

            if (!nextFriendSpeciesByName[speciesKey]) {
              nextFriendSpeciesByName[speciesKey] = {
                friendUIDs: [],
                forms: [],
              };
            }

            nextFriendSpeciesByName[speciesKey].friendUIDs.push(friendUID);

            const forms = Array.isArray(friendSpecies.forms)
              ? friendSpecies.forms
              : [];

            forms.forEach((form) => {
              nextFriendSpeciesByName[speciesKey].forms.push({
                ...form,
                ownerUID: friendUID,
                ownerName: profile?.name || "Unknown user",
              });
            });
          });
        });

        if (!cancelled) {
          setFriendProfiles(nextProfiles);
          setFriendSpeciesByName(nextFriendSpeciesByName);
        }
      } catch (error) {
        console.error("Failed to preload friend species:", error);

        if (!cancelled) {
          setFriendProfiles({});
          setFriendSpeciesByName({});
        }
      } finally {
        if (!cancelled) setFriendSpeciesLoading(false);
      }
    }

    if (!isOwnCollection || !currentUID || !genusName) {
      setFriendProfiles({});
      setFriendSpeciesByName({});
      setFriendSpeciesLoading(false);
      return () => {
        cancelled = true;
      };
    }

    loadFriendSpeciesForGenus();

    return () => {
      cancelled = true;
    };
  }, [currentUID, genusName, isOwnCollection]);

  useEffect(() => {
    let cancelled = false;

    async function loadSpecies() {
      setLoading(true);

      try {
        const normalizedGenus =
          genusName.charAt(0).toUpperCase() + genusName.slice(1);

        const speciesQuery = query(
          collection(db, "users", dataUID, "species"),
          where("genus", "==", normalizedGenus),
        );

        const snap = await getDocs(speciesQuery);
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        rows.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

        if (!cancelled) setSpeciesList(rows);
      } catch (err) {
        console.error("Failed to load species:", err);
        if (!cancelled) setSpeciesList([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (blockRelationshipLoading || waitingForAdminExploreCheck) {
      return () => {
        cancelled = true;
      };
    }

    if (collectionAccessBlocked) {
      setSpeciesList([]);
      setLoading(false);
    } else if (genusName) {
      loadSpecies();
    } else {
      setSpeciesList([]);
      setLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [
    genusName,
    dataUID,
    blockRelationshipLoading,
    waitingForAdminExploreCheck,
    collectionAccessBlocked,
    speciesRefreshKey,
  ]);

  // A previously opened photograph can have moved to an accepted genus or
  // species after an administrator-authorized revision. Only consult the
  // navigation alias when the originally requested species no longer exists in
  // this genus. This never restricts manual scientific-name entry.
  useEffect(() => {
    if (loading || !dataUID || collectionAccessBlocked) return undefined;
    const requestedId = String(location.state?.openSpeciesId ||
      searchParams.get("species") || "").trim().toLowerCase();
    if ((Number(location.state?.taxonomicRedirectHops) || 0) >= 5) return undefined;
    if (!requestedId || speciesList.some((sp) =>
      String(sp.id).toLowerCase() === requestedId ||
      String(sp.name).toLowerCase() === requestedId)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const redirect = await getDoc(doc(db, "taxonomicRedirects", requestedId));
        if (cancelled || !redirect.exists()) return;
        const accepted = redirect.data()?.acceptedScientificName;
        const nextRoute = taxonomicSpeciesRoute(accepted, uid || "");
        if (!nextRoute) return;
        const params = new URLSearchParams(location.search);
        const extra = params.get("adminMode") === "true" ? "&adminMode=true" : "";
        navigate(nextRoute + extra, {
          replace: true,
          state: { ...location.state,
            openSpeciesId: redirect.data().acceptedSpeciesId,
            taxonomicRedirectHops: (Number(location.state?.taxonomicRedirectHops) || 0) + 1 },
        });
      } catch (error) {
        console.warn("Could not resolve this outdated species link:", error);
      }
    })();
    return () => { cancelled = true; };
  }, [loading, dataUID, uid, collectionAccessBlocked, speciesList,
      location.state, location.search, searchParams, navigate]);

  useLayoutEffect(() => {
    if (loading) return;
    if (!location.state?.openSpeciesId) return;

    const targetSpecies = speciesList.find(
      (sp) => sp.id === location.state.openSpeciesId,
    );

    if (!targetSpecies) return;

    const forms = Array.isArray(targetSpecies.forms)
      ? targetSpecies.forms.slice().sort(defaultFirstSort)
      : [];

    const targetIndex = forms.findIndex(
      (form, idx) =>
        (form.id || form.url || `form-${idx}`) === location.state.openFormKey,
    );

    setSelected(targetSpecies);
    setPhotoIndex(targetIndex >= 0 ? targetIndex : 0);
  }, [loading, location.state, speciesList]);

  function slidePreviewPhoto(speciesId, nextIndex, direction, nextUrl) {
    if (!nextUrl) return;

    const img = new Image();
    img.src = nextUrl;

    img.onload = () => {
      setPreviewPhotoBySpecies((prev) => {
        const currentIndex = prev[speciesId] ?? 0;

        setPreviewSlideBySpecies((slidePrev) => ({
          ...slidePrev,
          [speciesId]: { from: currentIndex, to: nextIndex, direction },
        }));

        window.setTimeout(() => {
          setPreviewSlideBySpecies((slidePrev) => {
            const updated = { ...slidePrev };
            delete updated[speciesId];
            return updated;
          });
        }, 320);

        return {
          ...prev,
          [speciesId]: nextIndex,
        };
      });
    };
  }

  if (
    !isOwnCollection &&
    (blockRelationshipLoading || waitingForAdminExploreCheck)
  ) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8 sm:px-8">
        <div className="mx-auto flex min-h-80 max-w-2xl items-center justify-center rounded-3xl border border-[#93CA87]/40 bg-white shadow-xl">
          <div className="text-center">
            <div className="mx-auto h-9 w-9 animate-spin rounded-full border-4 border-[#93CA87]/40 border-t-[#24C3BE]" />

            <p className="mt-3 text-sm text-slate-500">Loading collection...</p>
          </div>
        </div>
      </div>
    );
  }

  if (collectionAccessBlocked) {
    return (
      <div className="min-h-screen bg-[#374049] px-3 py-8 sm:px-8">
        <div className="mx-auto max-w-2xl rounded-3xl border border-[#93CA87]/40 bg-white p-8 text-center shadow-xl">
          <h1
            className="text-4xl text-[#374049]"
            style={{ fontFamily: "Cormorant Garamond" }}
          >
            Collection Unavailable
          </h1>

          <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-slate-500">
            You cannot view or interact with this collection because there is a
            block between these accounts.
          </p>

          <Link
            to="/profile"
            className="mt-6 inline-flex rounded-xl bg-[#24C3BE] px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-[#1faeaa]"
          >
            Return to My Profile
          </Link>
        </div>
      </div>
    );
  }

  const speciesPhotoCount = speciesList.reduce(
    (total, species) => total + (Array.isArray(species.forms) ? species.forms.length : 0),
    0,
  );

  return (
    <>
      <style>
        {`
          @keyframes slideFromRight {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          @keyframes slideFromLeft {
            from { transform: translateX(-100%); }
            to { transform: translateX(0); }
          }
        `}
      </style>
      <BioIndexExploreLevelLayout
        uid={uid}
        dataUID={dataUID}
        currentLevel="species"
        params={{ kingdomName, phylumName, className, orderName, familyName, genusName }}
        browseMode={browseMode}
        setBrowseMode={setBrowseMode}
        isOwnCollection={isOwnCollection}
        itemName="species"
        itemCount={speciesList.length}
        secondaryCount={speciesPhotoCount}
        secondaryUnit="photos"
        adminModeActive={isAdminExploringCollection}
        modalOpen={Boolean(selected)}
        topContent={
          isAdminExploringCollection && (
            <div className="bioindex-explore-species-admin-panel">
              <div className="flex flex-col gap-3 rounded-2xl border border-[#24C3BE]/60 bg-[#FCFFF6] px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#374049] text-white">
                    <svg
                      viewBox="0 0 24 24"
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M12 3 19 6v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6l7-3Z" />
                    </svg>
                  </div>
    
                  <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#01767A]">
                      Admin Explore Mode
                    </div>
    
                    <p className="mt-1 text-sm text-[#374049]/70">
                      Administrative controls are available on every observation in
                      this collection.
                    </p>
                  </div>
                </div>
    
                <Link
                  to={`/profile/${dataUID}?adminMode=true`}
                  className="shrink-0 rounded-xl border border-[#374049] bg-white px-4 py-2 text-center text-xs font-semibold text-[#374049] transition hover:bg-[#374049] hover:text-white"
                >
                  Return to Managed Profile
                </Link>
              </div>
            </div>
          )
        }
      >
        {loading ? (
          <div className="bioindex-explore-empty" role="status">
            <span className="bioindex-explore-spinner" aria-hidden="true" />
            <h2>Loading species</h2>
          </div>
        ) : speciesList.length === 0 ? (
          <div className="bioindex-explore-empty">
            <h2>No species found</h2>
            <p>No observations have been documented for this genus yet.</p>
          </div>
        ) : (
          <div className="bioindex-explore-species-grid">
            {speciesList.map((sp) => {
              const forms = Array.isArray(sp.forms) ? sp.forms : [];
              const defaultForm = getDefaultForm(forms);
              const activeIndex = previewPhotoBySpecies[sp.id] ?? 0;
              const activeForm = forms[activeIndex] || defaultForm;
              const cardImg = activeForm?.url;
              const isImgLoaded = loadedImages[sp.id];
              const hasMultiplePhotos = forms.length > 1;
              const slideState = previewSlideBySpecies[sp.id];
              const previousForm =
                slideState && forms[slideState.from]
                  ? forms[slideState.from]
                  : null;
              const friendCount =
                friendSpeciesByName[normalizeSpeciesName(sp.name)]
                  ?.friendUIDs?.length || 0;

              return (
                <div
                  key={sp.id}
                  onClick={() => {
                    setSelected(sp);
                    setPhotoIndex(activeIndex);
                  }}
                  onKeyDown={(event) => {
                    if (event.target !== event.currentTarget) return;
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelected(sp);
                      setPhotoIndex(activeIndex);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Open ${sp.name || "species"} observations`}
                  className="bioindex-explore-species-card"
                >
                  <div className="relative w-full aspect-[4/3] flex items-center justify-center bg-white">
                    {!isImgLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-6 h-6 border-2 border-[#24c3be] border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}

                    {cardImg ? (
                      <div className="relative w-full h-full overflow-hidden flex items-center justify-center">
                        {slideState && previousForm && (
                          <img
                            src={previousForm.url}
                            alt=""
                            className={`absolute max-w-full max-h-full object-contain transition-all duration-300 ease-out ${
                              slideState.direction === "right"
                                ? "-translate-x-full opacity-0"
                                : "translate-x-full opacity-0"
                            }`}
                          />
                        )}

                        <img
                          key={cardImg}
                          src={cardImg}
                          alt={sp.name || "Species"}
                          loading="lazy"
                          onLoad={() =>
                            setLoadedImages((prev) => ({
                              ...prev,
                              [sp.id]: true,
                            }))
                          }
                          onError={() => {
                            console.error(
                              "Card image failed to load:",
                              sp.name,
                            );
                            setLoadedImages((prev) => ({
                              ...prev,
                              [sp.id]: true,
                            }));
                          }}
                          className={`absolute max-w-full max-h-full object-contain transition-all duration-300 ease-out ${
                            slideState
                              ? slideState.direction === "right"
                                ? "translate-x-0 animate-[slideFromRight_300ms_ease-out]"
                                : "translate-x-0 animate-[slideFromLeft_300ms_ease-out]"
                              : isImgLoaded
                                ? "opacity-100"
                                : "opacity-0"
                          }`}
                        />
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400">No photo</div>
                    )}

                    {friendCount > 0 && (
                      <div
                        title={`${friendCount} ${friendCount === 1 ? "friend has" : "friends have"} this species`}
                        className="absolute right-2 top-2 z-20 inline-flex h-7 items-center gap-1 rounded-full border border-white/70 bg-[#374049]/90 px-2 text-[10px] font-semibold text-white shadow-sm backdrop-blur-sm"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="h-3.5 w-3.5 text-[#93CA87]"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                          <circle cx="9" cy="7" r="4" />
                          <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                        </svg>
                        +{friendCount} {friendCount === 1 ? "friend" : "friends"}
                      </div>
                    )}

                    {hasMultiplePhotos && (
                      <div
                        className="absolute left-2 bottom-2 z-20 flex justify-start gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {forms.map((form, photoIndex) => (
                          <button
                            key={form.url || photoIndex}
                            type="button"
                            onClick={() =>
                              slidePreviewPhoto(
                                sp.id,
                                photoIndex,
                                photoIndex > activeIndex ? "right" : "left",
                                form.url,
                              )
                            }
                            className={`w-5 h-5 rounded overflow-hidden border bg-white/80 backdrop-blur-sm transition ${
                              photoIndex === activeIndex
                                ? "border-[#24c3be] ring-2 ring-[#24c3be]/30"
                                : "border-white/70 hover:border-[#24c3be]/70"
                            }`}
                          >
                            <img
                              src={form.url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="pt-3 pb-5 px-3">
                    <h3
                      className="text-xl italic text-[#374049]"
                      style={{ fontFamily: "Cormorant Garamond" }}
                    >
                      {sp.name || "—"}
                    </h3>
                    <p className="text-[#374049]/60 text-sm">
                      {sp.common || ""}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </BioIndexExploreLevelLayout>
      <SpeciesPhotoModal
        key={selected?.id || "closed-species-modal"}
        selected={selected}
        setSelected={setSelected}
        photoIndex={photoIndex}
        setPhotoIndex={setPhotoIndex}
        dataUID={dataUID}
        currentUID={currentUID}
        uid={uid}
        kingdomName={kingdomName}
        isOwnCollection={isOwnCollection}
        isAdminCollectionMode={isAdminExploringCollection}
        setSpeciesList={setSpeciesList}
        onRefresh={() => setSpeciesRefreshKey((currentKey) => currentKey + 1)}
        preloadedFriendSpeciesByName={friendSpeciesByName}
        preloadedFriendProfiles={friendProfiles}
        preloadedFriendSpeciesLoading={friendSpeciesLoading}
      />
    </>
  );
}
